// path: frontend/src/lib/share.ts

/** Utilities to prepare captions and share links for TikTok (and fallback).
 *  Note: TikTok does not allow direct web upload for all users; this helper
 *  focuses on composing a clean caption and using `navigator.share` where possible.
 */

export type ShareTarget = "tiktok" | "generic";

export interface ShareOptions {
  url: string;               // video URL
  title?: string;            // short title/topic
  hashtags?: string[];       // without '#'
  mode?: "tiktok" | "full"; // from app context
}

const TIKTOK_CAPTION_MAX = 150; // keep it punchy for mobile

export function normalizeHashtags(tags: string[] = [], max = 3): string[] {
  const unique = Array.from(new Set(tags.map(t => t.replace(/#/g, "").trim()).filter(Boolean)));
  return unique.slice(0, max);
}

export function buildTikTokCaption({ title = "", hashtags = [], mode = "tiktok" }: ShareOptions): string {
  const base = title.trim();
  const tags = normalizeHashtags(hashtags);
  const suffix = tags.length ? " " + tags.map(t => `#${t}`).join(" ") : "";
  const raw = (mode === "tiktok" ? base : `${base} | Full explainer`).slice(0, 2000) + suffix; // hard cap guard
  // Trim to a friendlier length for first-screen visibility
  if (raw.length <= TIKTOK_CAPTION_MAX) return raw;
  // truncate near word boundary
  const cut = raw.slice(0, TIKTOK_CAPTION_MAX - 1);
  return cut.replace(/\s\S*$/, "…");
}

/**
 * Share via Web Share API when available; fallback copies URL to clipboard.
 */
export async function shareVideo(opts: ShareOptions): Promise<void> {
  const caption = buildTikTokCaption(opts);
  try {
    if (navigator.share) {
      await navigator.share({ title: opts.title || "AutoQuiz AI", text: caption, url: opts.url });
      return;
    }
  } catch {/* user canceled; ignore */}

  try {
    await navigator.clipboard.writeText(opts.url);
    alert("Link copied. Open TikTok and paste your caption.");
  } catch {
    // Very old browsers: show prompt
    window.prompt("Copy this link", opts.url);
  }
}
